/**
 * Twist bone driver — automatically applies partial twist from parent bones.
 *
 * Twist bones sit alongside their parent deform bone (e.g. arm_twist next to
 * upper_arm) and take on a fraction of the parent's Y-axis twist rotation.
 * This distributes limb-twist deformation across a larger area of the mesh,
 * preventing the "candy wrapper" artifact at joints.
 *
 * The driver runs every frame after user bone poses are applied and before
 * corrective shape key evaluation.
 */

import * as THREE from "three/webgpu";
import { getRestPose } from "./boneRegistry.ts";
import { decomposeSwingTwist } from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Twist bone definitions
// ---------------------------------------------------------------------------

interface TwistBoneDef {
  twist: string;
  parent: string;
  factor: number;
}

const TWIST_BONE_DEFS: readonly TwistBoneDef[] = [
  { twist: "arm_twistL", parent: "upper_armL", factor: 0.5 },
  { twist: "arm_twistR", parent: "upper_armR", factor: 0.5 },
  { twist: "forearm_twistL", parent: "forearmL", factor: 0.5 },
  { twist: "forearm_twistR", parent: "forearmR", factor: 0.5 },
  { twist: "thigh_twistL", parent: "thighL", factor: 0.5 },
  { twist: "thigh_twistR", parent: "thighR", factor: 0.5 },
  { twist: "leg_twistL", parent: "shinL", factor: 0.5 },
  { twist: "leg_twistR", parent: "shinR", factor: 0.5 },
];

/** Set of all twist bone names — used to exclude them from IK, pose save, etc. */
export const TWIST_BONE_NAMES: ReadonlySet<string> = new Set(
  TWIST_BONE_DEFS.map((d) => d.twist),
);

// ---------------------------------------------------------------------------
// Reusable temporaries (avoid per-frame allocation)
// ---------------------------------------------------------------------------

const _parentRest = new THREE.Quaternion();
const _parentCurrent = new THREE.Quaternion();
const _delta = new THREE.Quaternion();
const _swing = new THREE.Quaternion();
const _twist = new THREE.Quaternion();
const _twistRest = new THREE.Quaternion();
const _result = new THREE.Quaternion();
const _yAxis = new THREE.Vector3(0, 1, 0);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply twist bone rotations derived from their parent bones.
 *
 * For each twist bone, extracts the Y-axis twist component from the parent
 * bone's delta-from-rest rotation, scales it by the factor, and applies it
 * on top of the twist bone's own rest pose.
 *
 * Must be called after all user bone poses are set and before corrective
 * shape key evaluation.
 */
export function applyTwistBones(
  objectId: string,
  boneMap: Map<string, THREE.Bone>,
): void {
  for (const def of TWIST_BONE_DEFS) {
    const twistBone = boneMap.get(def.twist);
    if (!twistBone) continue;

    const parentBone = boneMap.get(def.parent);
    if (!parentBone) continue;

    // Get parent's rest and current quaternion
    const parentRestQ = getRestPose(objectId, def.parent);
    const twistRestQ = getRestPose(objectId, def.twist);
    if (!parentRestQ || !twistRestQ) continue;

    _parentRest.set(
      parentRestQ[0],
      parentRestQ[1],
      parentRestQ[2],
      parentRestQ[3],
    );
    _twistRest.set(twistRestQ[0], twistRestQ[1], twistRestQ[2], twistRestQ[3]);

    // Use the parent's actual current quaternion (already set on the bone)
    _parentCurrent.copy(parentBone.quaternion);

    // delta = inverse(rest) * current
    _delta.copy(_parentRest).invert().premultiply(_parentCurrent);

    // Extract twist component around Y axis
    decomposeSwingTwist(_delta, _yAxis, _swing, _twist);

    // Scale the twist by the factor: slerp(identity, twist, factor)
    _result.set(0, 0, 0, 1).slerp(_twist, def.factor);

    // Final = restPose * scaledTwist
    twistBone.quaternion.copy(_twistRest).multiply(_result);
  }
}
